/**
 * Locale-aware lookup over the partner_embedded_instance_strings rows
 * returned in /embedded/resolve.
 *
 * Resolution order (high → low):
 *   1. Row whose `lang` exactly equals the requested locale (e.g. 'pt-BR').
 *   2. Row whose `lang` matches the base of the requested locale
 *      (e.g. 'pt' for a 'pt-BR' request).
 *   3. The English (`en`) row.
 *   4. `undefined`.
 *
 * Caller decides what to do with `undefined` — typically falls back to a
 * legacy single-language field (theme.launcher.label, i18n.suggestions)
 * for instances that haven't migrated yet, or to a build-time default.
 */
import type { ResolvedInstanceConfig } from '../api/instanceApi';

type StringsRow = NonNullable<ResolvedInstanceConfig['strings']>[number];

function findRow(
  strings: StringsRow[] | undefined,
  lang: string,
): StringsRow | undefined {
  if (!strings || strings.length === 0) return undefined;
  return strings.find(s => s.lang === lang);
}

export function pickLocalizedString<T = string>(
  strings: ResolvedInstanceConfig['strings'] | undefined,
  field: string,
  locale: string | undefined | null,
): T | undefined {
  if (!strings || strings.length === 0) return undefined;
  const tag = (locale || 'en').trim();

  // 1. Exact match (full BCP-47 tag).
  let row = findRow(strings, tag);
  if (row && row.payload && row.payload[field] !== undefined) {
    return row.payload[field] as T;
  }

  // 2. Base language match (strip region).
  const base = tag.split('-')[0];
  if (base && base !== tag) {
    row = findRow(strings, base);
    if (row && row.payload && row.payload[field] !== undefined) {
      return row.payload[field] as T;
    }
  }

  // 3. English fallback.
  row = findRow(strings, 'en');
  if (row && row.payload && row.payload[field] !== undefined) {
    return row.payload[field] as T;
  }

  return undefined;
}
